import type { CSSProperties } from "react"

import { cuerpoQueEntra } from "./ajuste"
import { pt, TINTA } from "./medidas"

/**
 * Las piezas con las que se dibujan los formatos oficiales.
 *
 * ⚠ **Todo va POSICIONADO EN ABSOLUTO sobre la hoja, y es deliberado.** El resto del
 * sistema maqueta con flex y grid, que es lo correcto cuando el diseño es nuestro.
 * Aquí no lo es: hay que reproducir un documento que ya existe, y una retícula
 * reinterpretada se parece al original sin coincidir — la caja de «N° DE REGISTRO»
 * acaba dos milímetros a la izquierda y nadie sabe decir por qué. Con las
 * coordenadas del propio PDF, cada cosa cae donde cae en el papel del funcionario.
 *
 * ⚠ **Se escribe DIRECTO sobre el formato, no en un formulario aparte.** Era la
 * elección que quedaba abierta y ésta es la razón: un formulario al lado obliga a
 * mirar en dos sitios y a confiar en que la etiqueta de la izquierda corresponde a la
 * casilla de la derecha. Sobre el formato, la casilla donde se escribe ES la casilla
 * que se imprime — la misma regla que sostiene la rotulación y los carnets, llevada
 * hasta el final.
 */

/** Un rótulo del formato: texto fijo, en el azul medio del original. */
export function Rotulo({
  x,
  y,
  children,
  tamano = 10,
  centrado,
  tinta = TINTA.rotulo,
  negrita = false,
}: {
  /** En puntos, desde el borde izquierdo de la hoja. */
  x: number
  /** En puntos, desde el borde superior. Es la línea BASE alta del texto. */
  y: number
  children: string
  tamano?: number
  /** Si se pasa, el texto se centra en una caja de este ancho que empieza en `x`. */
  centrado?: number
  tinta?: string
  negrita?: boolean
}) {
  return (
    <span
      style={{
        position: "absolute",
        left: `${pt(x)}mm`,
        top: `${pt(y)}mm`,
        width: centrado === undefined ? undefined : `${pt(centrado)}mm`,
        textAlign: centrado === undefined ? undefined : "center",
        fontSize: `${pt(tamano)}mm`,
        lineHeight: 1.25,
        fontWeight: negrita ? 700 : 400,
        color: tinta,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  )
}

/**
 * Una casilla rellenable: el rectángulo azul claro del original, con un campo dentro.
 *
 * ⚠ **El relleno azul se IMPRIME.** Es como es el formato oficial, y `.pieza-impresa`
 * fuerza `print-color-adjust: exact` para que el navegador no lo quite «para ahorrar
 * tinta». Un formato con las casillas en blanco no es el formato: es una fotocopia
 * mala de él, y quien lo recibe tiene que fiarse de que los huecos están donde deben.
 *
 * ⚠ **El campo no tiene borde ni fondo propios: los pone la caja.** Si el `input`
 * trajera los suyos, al imprimir habría dos rectángulos desalineados por un píxel.
 *
 * ⚠ **El cuerpo de letra baja solo cuando lo escrito no cabe.** Ver `ajuste.ts`: con
 * el cuerpo fijo, la razón social de la agencia salía recortada. El hueco que se
 * mide es el ancho de la caja menos su relleno, 3 pt por lado.
 */
export function Caja({
  x,
  y,
  w,
  h,
  valor,
  onCambio,
  etiqueta,
  centrado = false,
  cifra = false,
  tamano = 10,
}: {
  x: number
  y: number
  w: number
  h: number
  valor: string
  onCambio: (v: string) => void
  /** Para el lector de pantalla y para las pruebas: el formato no tiene `<label>`. */
  etiqueta: string
  centrado?: boolean
  /** Montos y cantidades, con `tabular-nums` (regla 10). */
  cifra?: boolean
  tamano?: number
}) {
  const { cuerpo, cabe } = cuerpoQueEntra(valor, w - RELLENO * 2, tamano)
  return (
    <input
      type="text"
      aria-label={etiqueta}
      value={valor}
      onChange={(e) => onCambio(e.target.value)}
      className={clases(cifra, cabe)}
      title={cabe ? undefined : NO_CABE}
      style={{
        position: "absolute",
        left: `${pt(x)}mm`,
        top: `${pt(y)}mm`,
        width: `${pt(w)}mm`,
        height: `${pt(h)}mm`,
        background: TINTA.caja,
        color: TINTA.oscura,
        fontSize: `${pt(cuerpo)}mm`,
        textAlign: centrado ? "center" : "left",
        padding: `0 ${pt(RELLENO)}mm`,
      }}
    />
  )
}

/** El relleno lateral de cada campo, en pt. Se descuenta del hueco al medir. */
const RELLENO = 3

/** Lo que dice el campo que no cabe, al pasar el ratón por encima. */
const NO_CABE = "No cabe entero ni con la letra más pequeña: acórtalo antes de imprimir."

/** Las clases de un campo, según lleve cifras y quepa o no. */
function clases(cifra: boolean, cabe: boolean): string {
  return ["campo-plantilla", cifra ? "tabular" : "", cabe ? "" : "campo-plantilla-no-cabe"]
    .filter(Boolean)
    .join(" ")
}

/**
 * Una de las ocho casillas de régimen, que se marca con una equis.
 *
 * ⚠ **Es un `checkbox` de verdad, no un `div` que cambia de color.** Se alcanza con
 * el tabulador, se marca con la barra espaciadora y un lector de pantalla dice si
 * está marcada. Lo que se ve —el cuadro blanco y la equis— lo pinta el `label` que lo
 * envuelve, porque un `checkbox` nativo no se puede restilar del todo y menos al
 * imprimir.
 */
export function Marca({
  x,
  y,
  w,
  h,
  marcada,
  onCambio,
  etiqueta,
}: {
  x: number
  y: number
  w: number
  h: number
  marcada: boolean
  onCambio: (v: boolean) => void
  etiqueta: string
}) {
  const caja: CSSProperties = {
    position: "absolute",
    left: `${pt(x)}mm`,
    top: `${pt(y)}mm`,
    width: `${pt(w)}mm`,
    height: `${pt(h)}mm`,
    background: TINTA.papel,
    color: TINTA.oscura,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: `${pt(h * 0.8)}mm`,
    fontWeight: 700,
    lineHeight: 1,
    cursor: "pointer",
  }
  return (
    <label style={caja}>
      <input
        type="checkbox"
        checked={marcada}
        aria-label={etiqueta}
        onChange={(e) => onCambio(e.target.checked)}
        className="marca-plantilla"
      />
      {marcada ? "X" : ""}
    </label>
  )
}

/**
 * Un renglón del pie: la etiqueta y la raya sobre la que se escribe.
 *
 * ⚠ **La raya es el borde inferior de un campo, y no la fila de guiones bajos del
 * original.** Impreso se ve igual —una línea continua bajo el texto— y encima se
 * puede escribir, que es lo que el original no dejaba hacer. Con los guiones, el
 * valor tendría que ir flotando por encima y desalineado con el trazo.
 *
 * ⚠ **La firma no lleva campo.** Se firma a mano, con un bolígrafo, después de
 * imprimir: poner ahí una caja de texto invita a teclear un nombre donde hace falta
 * una rúbrica.
 */
export function Renglon({
  x,
  y,
  ancho,
  etiqueta,
  valor,
  onCambio,
}: {
  x: number
  y: number
  ancho: number
  etiqueta: string
  /** Sin `valor` el renglón sale sólo con su raya: es el de la firma. */
  valor?: string
  onCambio?: (v: string) => void
}) {
  const alto = 14
  const { cuerpo, cabe } = cuerpoQueEntra(valor ?? "", ancho - RELLENO * 2, 11.2)
  return (
    <>
      <span
        style={{
          position: "absolute",
          left: 0,
          top: `${pt(y)}mm`,
          width: `${pt(x)}mm`,
          textAlign: "right",
          fontSize: `${pt(11.2)}mm`,
          lineHeight: 1.25,
          color: TINTA.oscura,
          whiteSpace: "nowrap",
        }}
      >
        {etiqueta}
      </span>
      {valor === undefined || onCambio === undefined ? (
        <span
          style={{
            position: "absolute",
            left: `${pt(x)}mm`,
            top: `${pt(y + alto)}mm`,
            width: `${pt(ancho)}mm`,
            borderBottom: `0.35mm solid ${TINTA.oscura}`,
          }}
        />
      ) : (
        <input
          type="text"
          aria-label={etiqueta.replace(":", "")}
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
          className={clases(false, cabe)}
          title={cabe ? undefined : NO_CABE}
          style={{
            position: "absolute",
            left: `${pt(x)}mm`,
            top: `${pt(y - 1)}mm`,
            width: `${pt(ancho)}mm`,
            height: `${pt(alto + 1)}mm`,
            borderBottom: `0.35mm solid ${TINTA.oscura}`,
            color: TINTA.oscura,
            fontSize: `${pt(cuerpo)}mm`,
            padding: `0 ${pt(RELLENO)}mm`,
          }}
        />
      )}
    </>
  )
}

/** La nota al pie derecho, en dos líneas y en el azul oscuro. */
export function Nota({ x, y, lineas }: { x: number; y: number; lineas: string[] }) {
  return (
    <div
      style={{
        position: "absolute",
        left: `${pt(x)}mm`,
        top: `${pt(y)}mm`,
        fontSize: `${pt(8.7)}mm`,
        lineHeight: 1.15,
        color: TINTA.oscura,
        // ⚠ `pre` porque el original escribe «NOTA:  DE SER…» con DOS espacios, y
        // HTML los colapsa en uno. Es literalmente lo que dice el papel del SENIAT.
        whiteSpace: "pre",
      }}
    >
      {lineas.map((linea) => (
        <div key={linea}>{linea}</div>
      ))}
    </div>
  )
}
